import type { ReactNode } from 'react'
import { Icon } from '@iconify/react'
import {
  formatCompactDate,
  formatCurrency,
  formatNumber,
  getCampaignDisplayName,
  getStatusLabel,
} from '@/lib/campaign-dashboard'
import type { CampaignDashboardCampaign } from '@/types/campaign-report'
import styles from './CampaignDashboard.module.css'

interface CampaignInfoPanelProps {
  campaign: CampaignDashboardCampaign
}

function InfoItem({ label, value }: { label: string; value: string | null | undefined }) {
  return (
    <div className={styles.infoItem}>
      <span className={styles.infoItemLabel}>{label}</span>
      <span className={styles.infoItemValue}>{value || '-'}</span>
    </div>
  )
}

function InfoSection({
  title,
  children,
}: {
  title: string
  children: ReactNode
}) {
  return (
    <section className={styles.infoSection}>
      <h3 className={styles.sectionTitle}>{title}</h3>
      <div className={styles.infoList}>{children}</div>
    </section>
  )
}

export function CampaignInfoPanel({ campaign }: CampaignInfoPanelProps) {
  const publicUrl = campaign.slug ? `/campanha/${campaign.slug}` : null

  return (
    <section className={styles.infoCard}>
      <div className={styles.infoHeader}>
        <div>
          <h2 className={styles.sectionTitle}>Informacoes completas da campanha</h2>
          <p className={styles.sectionSubtitle}>
            Dados administrativos, comerciais e de configuracao retornados pela API.
          </p>
        </div>
      </div>

      <div className={styles.infoStack}>
        <InfoSection title="Identidade">
          <InfoItem label="Nome exibido" value={getCampaignDisplayName(campaign)} />
          <InfoItem label="Nome interno" value={campaign.name} />
          <InfoItem label="Slug" value={campaign.slug ?? '-'} />
          <InfoItem label="Status" value={getStatusLabel(campaign.status)} />
          <InfoItem label="Inicio" value={formatCompactDate(campaign.starts_at)} />
          <InfoItem label="Fim" value={formatCompactDate(campaign.ends_at)} />
        </InfoSection>

        <InfoSection title="Comercial">
          <InfoItem label="Preco por numero" value={formatCurrency(campaign.price)} />
          <InfoItem label="Total de numeros" value={formatNumber(campaign.total_numbers)} />
          <InfoItem
            label="Minimo por compra"
            value={formatNumber(campaign.min_quantity_per_purchase)}
          />
          <InfoItem
            label="Maximo por compra"
            value={formatNumber(campaign.max_quantity_per_purchase)}
          />
          <InfoItem label="Doey seller" value={campaign.doey_seller_id ?? '-'} />
          <InfoItem label="CTA" value={campaign.cta_text ?? '-'} />
        </InfoSection>

        <InfoSection title="Links e documentos">
          <InfoItem label="Landing" value={publicUrl ?? '-'} />
          <InfoItem label="WhatsApp" value={campaign.whatsapp_button_url ?? '-'} />
          <InfoItem label="Login" value={campaign.login_url ?? '-'} />
          <InfoItem label="Video" value={campaign.youtube_video_url ?? '-'} />
          <InfoItem label="Regulamento PDF" value={campaign.regulation_pdf_url ?? '-'} />
          <InfoItem label="Certificado PDF" value={campaign.certificate_pdf_url ?? '-'} />
          <InfoItem
            label="Numero do certificado"
            value={campaign.certificate_number ?? '-'}
          />
        </InfoSection>

        <InfoSection title="Pixels e rastreamento">
          <InfoItem label="Facebook Pixel" value={campaign.fb_pixel ?? '-'} />
          <InfoItem
            label="Token da API de Conversão"
            value={campaign.fb_access_token?.trim() || '-'}
          />
          <InfoItem label="Google Pixel / GTM" value={campaign.g_pixel ?? '-'} />
          <InfoItem
            label="GA Measurement ID"
            value={campaign.gtm_measurement_id ?? '-'}
          />
          <InfoItem
            label="Segredo do Measurement Protocol (GA4)"
            value={campaign.ga4_measurement_protocol_secret?.trim() || '-'}
          />
          <InfoItem label="Script GTM" value={campaign.script_gtm ? 'Configurado' : '-'} />
        </InfoSection>

        <section className={styles.infoSection}>
          <div className={styles.infoHeader}>
            <div>
              <h3 className={styles.sectionTitle}>Paleta visual</h3>
              <p className={styles.sectionSubtitle}>
                Cores configuradas na landing e nos materiais da campanha.
              </p>
            </div>
          </div>
          <div className={styles.colorSwatches}>
            {[
              { label: 'Primaria', value: campaign.primary_color },
              { label: 'Secundaria', value: campaign.secondary_color },
              { label: 'Botao', value: campaign.button_color },
              { label: 'Destaque', value: campaign.highlight_color },
            ].map((item) => (
              <div key={item.label} className={styles.swatch}>
                <span
                  className={styles.swatchPreview}
                  style={{ backgroundColor: item.value || '#e2e8f0' }}
                />
                <div>
                  <div className={styles.tableName}>{item.label}</div>
                  <div className={styles.tableMuted}>{item.value || 'Nao definida'}</div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {(campaign.description || campaign.prize_description) && (
          <div className={styles.infoTextSections}>
            <section className={styles.infoSection}>
              <h3 className={styles.sectionTitle}>Descricao</h3>
              <p className={styles.stateText}>
                {campaign.description || 'Sem descricao cadastrada.'}
              </p>
            </section>
            <section className={styles.infoSection}>
              <h3 className={styles.sectionTitle}>Premio</h3>
              <p className={styles.stateText}>
                {campaign.prize_description || 'Sem descricao de premio cadastrada.'}
              </p>
            </section>
            <section className={styles.infoSection}>
              <h3 className={styles.sectionTitle}>E-mails transacionais</h3>
              <p className={styles.stateText}>Padrão da plataforma Sortee</p>
            </section>
          </div>
        )}

        {campaign.main_image_url && (
          <div className={styles.tableMeta}>
            <span className={styles.badgeInfo}>
              <Icon icon="mdi:image-outline" />
              Imagem principal configurada
            </span>
          </div>
        )}
      </div>
    </section>
  )
}
